import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ComponentBase } from 'src/app/components/core/_infrastructure/classes/componentBase';
import { AuthService } from 'src/app/components/core/_infrastructure/classes/services/auth.service';
import { NotificationService } from 'src/app/components/core/_infrastructure/classes/services/notification.service';
import { TwSolicitudCamViewModel } from '../../../types/solicitudcam-datosbasicos.types';
import { ExceptionManager } from 'src/app/components/core/_infrastructure/classes/handlers/exceptionManager';
import { CASelectedViewModel, PeriodoDatosCaCamViewModel, PeriodoTipoSolicitudViewModel } from '../../../buscadores/solicitudes/buscador-solicitud-cam.types';
import { FilterSearchingBaseViewModel, Tc } from 'src/app/components/core/_infrastructure/types/common';
import { ForeignKeyValidator } from 'src/app/components/core/_infrastructure/classes/foreignKeyValidator';
import { VariedadItemResultsViewModel } from 'src/app/components/_mantenimiento/especies/variedades/variedad.types';
import {
    DialogSelectionMode,
    EnumDescripcionTcTipoSolicitudCamPlural,
    EnumDescripcionTcTipoSolicitudCamSingular,
    EnumIdsTcTipoSolicitudCam,
} from '../../../types/enum';
import { UsuarioViewModel } from 'src/app/components/_mantenimiento/usuarios/usuarios.types';

@Component({
    selector: 'app-solicitud-cam-edit',
    templateUrl: './solicitud-cam-edit.component.html',
    styleUrls: ['./solicitud-cam-edit.component.scss'],
})
export class SolicitudCamEditComponent extends ComponentBase implements OnChanges, OnInit {
    @Input() readonly: boolean;
    @Input() variedades: VariedadItemResultsViewModel;
    @Input() solicitudCam: TwSolicitudCamViewModel;
    @Input() campanias: Tc[];
    @Input() periodos: Tc[];
    @Input() CAs: PeriodoDatosCaCamViewModel[];
    @Input() tiposSolicitud: PeriodoTipoSolicitudViewModel[];
    @Input() EAs: Tc[];
    @Input() usuarioExterno: UsuarioViewModel;
    @Input() formGroup: FormGroup;
    @Output() campaniaSelected = new EventEmitter<number>();
    @Output() periodoSelected = new EventEmitter<number>();
    @Output() caSelected = new EventEmitter<CASelectedViewModel>();

    isCollapsedCabecera = false;
    isCollapsedDatosOperador = false;
    isCollapsedVariedades = false;

    variedadesLastFilter: FilterSearchingBaseViewModel;

    constructor(
        protected notificationService: NotificationService,
        protected authService: AuthService,
        protected exceptionHandler: ExceptionManager,
    ) {
        super(authService, notificationService, exceptionHandler);
    }

    ngOnInit(): void {
        this.updateForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes?.solicitudCam?.currentValue) {
            this.solicitudCam = { ...changes.solicitudCam.currentValue };
            this.updateForm();
        }
        if (changes?.CAs?.currentValue) {
            if (this.solicitudCam) {
                const selectedCA: PeriodoDatosCaCamViewModel | undefined = this.CAs?.find(x => x.IdCa == this.solicitudCam.IdCa);
                this.entidadAuditoraEnabledForCA = selectedCA?.EntidadAuditora || false;
            }
        }
        if (changes?.variedades?.currentValue) {
            this.prepareVariedadesLastFilter();
        }
        if (changes?.usuarioExterno?.currentValue) {
            this.setUsuarioExterno();
        }
    }

    private setUsuarioExterno(): void {
        if (!this.formGroup) {
            return;
        }
        this.formGroup.patchValue({
            OperadorRazonSocial: this.usuarioExterno?.TwUsuarioExterno.RazonSocial || '',
            OperadorNif: this.usuarioExterno?.TwUsuarioExterno.NifCifRazonSocial || '',
            OperadorTelefono: this.usuarioExterno?.TwUsuarioExterno.NumeroTelefono || '',
        });
    }

    private prepareVariedadesLastFilter(): void {
        this.variedadesLastFilter = new FilterSearchingBaseViewModel();
        this.variedadesLastFilter.PageNumber = this.variedades?.PageCount;
    }

    private manageEAValidation(): void {
        if (this.entidadAuditoraEnabledForCA) {
            this.formGroup.controls['IdEntidadAuditora'].addValidators(ForeignKeyValidator.cannotBeNullOrZero);
        } else {
            this.formGroup.controls['IdEntidadAuditora'].removeValidators(ForeignKeyValidator.cannotBeNullOrZero);
        }
    }

    /**
     * This functions sets the values in the solicituCam (@Input) into the form.
     */
    private updateForm(): void {
        this.formGroup.setValue({
            Id: this.solicitudCam?.Id || null,
            IdCampania: this.solicitudCam?.IdCampania || null,
            IdPeriodoCam: this.solicitudCam?.IdPeriodoCam || null,
            IdCA: this.solicitudCam?.IdCa || null,
            IdEntidadAuditora: this.solicitudCam?.IdEntidadAuditora || null,
            IdTipoSolicitudCam: this.solicitudCam?.IdTipoSolicitudCam || null,
            // The following are read only but will be shown on screen.
            Codigo: this.solicitudCam?.Codigo || '',
            FechaSolicitud: this.solicitudCam?.FechaSolicitud ? new Date(this.solicitudCam?.FechaSolicitud) : '',
            EstadoSolicitudCam: this.solicitudCam?.EstadoSolicitudCam || '',
            // Not visible but needed
            IdPeriodoPlazoCam: this.solicitudCam?.IdPeriodoPlazoCam || null,
            IdAlmacenExportacionCicloCorto: this.solicitudCam?.IdAlmacenExportacionCicloCorto || null,
            IdEstadoSolicitudCam: this.solicitudCam?.IdEstadoSolicitudCam || null,
            FechaCreacion: this.solicitudCam?.FechaCreacion || '',
            IdUsuario: this.solicitudCam?.IdUsuario || null,
            OperadorRazonSocial: this.solicitudCam?.OperadorRazonSocial || '',
            OperadorNif: this.solicitudCam?.OperadorNif || '',
            OperadorTelefono: this.solicitudCam?.OperadorTelefono || '',
        });

        this.form = this.formGroup; // componentBase uses this.form
    }

    /**
     * Manejador que permite emitir el evento que indica que ha cambiado la Campaña.
     * @param idCampania number
     */
    onSelectCampania(idCampania: number): void {
        this.campaniaSelected.emit(idCampania);
        this.cleanDependentOnCampania();
    }

    onSelectPeriodo(idPeriodo: number | null): void {
        this.periodoSelected.emit(idPeriodo!);
        this.cleanDependentOnPeriodo();
    }

    /**
     * Manejador del evento de selección de una CA en el desplegable.
     * Ignoramos el valor que viene en el parámetro y lo tomamos del form.
     * @param caSelected PeriodoDatosCaCamViewModel | undefined
     */
    onSelectCA(caSelected: PeriodoDatosCaCamViewModel | undefined): void {
        if (caSelected && caSelected.EntidadAuditora) {
            const caSelectedVM = new CASelectedViewModel();
            caSelectedVM.idCA = caSelected.IdCa;
            caSelectedVM.idPeriodo = this.form.controls.IdPeriodoCam.value;

            this.caSelected.emit(caSelectedVM);
        }

        this.entidadAuditoraEnabledForCA = caSelected?.EntidadAuditora || false;
        this.setEntidadAuditora(null);
    }

    private cleanDependentOnCampania(): void {
        this.setPeriodo(null);
        this.cleanDependentOnPeriodo();
    }

    private cleanDependentOnPeriodo(): void {
        this.entidadAuditoraEnabledForCA = false;
        this.setComunidadAutonoma(null);
        this.setEntidadAuditora(null);
        this.setTipoSolicitud(null);
    }

    private setPeriodo(idPeriodoCam: number | null): void {
        this.form.patchValue({
            IdPeriodoCam: idPeriodoCam,
        });
    }

    private setComunidadAutonoma(idCA: number | null): void {
        this.form.patchValue({
            IdCA: idCA,
        });
    }

    private setTipoSolicitud(idTipoSolicitudCam: number | null): void {
        this.form.patchValue({
            IdTipoSolicitudCam: idTipoSolicitudCam,
        });
    }

    private setEntidadAuditora(idEntidadAuditora: number | null): void {
        this.form.patchValue({
            IdEntidadAuditora: idEntidadAuditora,
        });
    }

    get title(): string {
        return this.solicitudCam ? (this.readonly ? 'Detalle de la' : 'Editar') : 'Nueva'; // work mode: readonly, edition or creation
    }

    get EANoHabilitada(): boolean {
        return (
            this.CAs?.length > 0 &&
            this.form.controls.IdCampania.value !== null &&
            this.form.controls.IdCampania.value !== undefined &&
            this.form.controls.IdCampania.value !== 0 &&
            this.form.controls.IdPeriodoCam.value !== null &&
            this.form.controls.IdPeriodoCam.value !== undefined &&
            this.form.controls.IdPeriodoCam.value !== 0 &&
            this.form.controls.IdCA.value !== null &&
            this.form.controls.IdCA.value !== undefined &&
            this.form.controls.IdCA.value !== 0 &&
            !this.entidadAuditoraEnabledForCA
        );
    }

    private EAEnabledForCA = true;

    get entidadAuditoraEnabledForCA(): boolean {
        return this.EAEnabledForCA;
    }

    set entidadAuditoraEnabledForCA($val: boolean) {
        this.EAEnabledForCA = $val;
        this.manageEAValidation();
    }

    get hasRecintosPIAED(): boolean {
        return this.solicitudCam?.HasRecintosPIAED || false;
    }

    get dialogSelectionMode(): string {
        return DialogSelectionMode.None;
    }

    get esTipoRecintosPI(): boolean {
        return (
            this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_INVERNADERO ||
            this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_PARCELA
        );
    }

    get vinculadosMessage(): string {
        if (!this.solicitudCam) return '';
        return this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_PARCELA ||
            this.solicitudCam?.IdTipoSolicitudCam === EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION
            ? 'vinculadas'
            : 'vinculados';
    }

    get titleByTipoSolicitud(): any {
        if (!this.solicitudCam)
            return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };

        switch (this.solicitudCam?.IdTipoSolicitudCam) {
            case EnumIdsTcTipoSolicitudCam.ID_CONFECCION:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.CONFECCION, singular: EnumDescripcionTcTipoSolicitudCamSingular.CONFECCION };
            case EnumIdsTcTipoSolicitudCam.ID_EMPRESA_DESHIDRATACION:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.EMPRESA_DESHIDRATACION,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.EMPRESA_DESHIDRATACION,
                };
            case EnumIdsTcTipoSolicitudCam.ID_PARCELA:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.PARCELA,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.PARCELA,
                };
            case EnumIdsTcTipoSolicitudCam.ID_INVERNADERO:
                return {
                    plural: EnumDescripcionTcTipoSolicitudCamPlural.INVERNADERO,
                    singular: EnumDescripcionTcTipoSolicitudCamSingular.INVERNADERO,
                };
            default:
                return { plural: EnumDescripcionTcTipoSolicitudCamPlural.RECINTOS, singular: EnumDescripcionTcTipoSolicitudCamSingular.RECINTO };
        }
    }
}
